import React, {useState, useMemo} from 'react'
import {InputGroup, FormControl, Row, Col, Form} from 'react-bootstrap'
import {format} from 'date-fns'
import CRUDTable from '../table/CRUDTable'
import {StudentDailyTripAPIData} from '../../app/ts/trip'
import ViewAll from '../ViewAll'
import WithModal from '../WithModal'
import Driver from '../../app/pages/driver/Index'
import viewProps from '../../app/pages/viewProps'
import StudentDetails from '../StudentDetails'
import GuardianDetails from '../GuardianDetails'

interface StudentsInDailyTripTableProps {
  studentData: StudentDailyTripAPIData[]
  fieldsToLableMap: {}
}

const StudentsInDailyTripTable: React.FC<StudentsInDailyTripTableProps> = ({
  studentData,
  fieldsToLableMap,
}) => {
  const [searchInput, setSearchInput] = useState('')
  const [filterOption, setFilterOption] = useState('')
  // Function to format time using date-fns
  const formatTime = (time: string | null): string => {
    if (!time) return 'N/A'
    try {
      const parsedTime = new Date(`1970-01-01T${time}Z`) // Using a dummy date
      return format(parsedTime, 'HH:mm aa') // Format time to HH:mm:ss
    } catch (error) {
      return 'Invalid time' // In case the time is not in the expected format
    }
  }

  const cellModifier = {
    student_name: ({row, value}: any) => {
      return (
        <WithModal
          renderModalBody={() => <StudentDetails studentId={row.original.student_id} />}
          modalTitle={
            <span>
              Details of <span className="text-primary">{value}</span>
            </span>
          }
          modalAttrs={{backdrop: true, size: 'lg'}}
        >
          <span>
            {value}
            <i className="fa-regular fa-folder-open fs-4 d-inline-block ps-1"></i>
          </span>
        </WithModal>
      )
    },
    parent_name: ({row, value}: {row: {original: StudentDailyTripAPIData}; value: string}) => {
      return (
        <WithModal
          renderModalBody={() => <GuardianDetails guardianId={row.original.parent_id} />}
          modalTitle={
            <span>
              Guradian Details of <span className="text-primary">{value}</span>
            </span>
          }
          modalAttrs={{backdrop: true, size: 'lg'}}
        >
          <span>
            {value}
            <i className="fa-regular fa-folder-open fs-4 d-inline-block ps-1"></i>
          </span>
        </WithModal>
      )
    },
    is_manual_checkin: ({value}: any) => {
      return value === '1' ? 'Yes' : 'No'
    },
    is_manual_checkout: ({value}: any) => {
      return value === '1' ? 'Yes' : 'No'
    },
    is_manual_markabsent: ({value}: any) => {
      return value === '1' ? 'Yes' : 'No'
    },
    is_wrong_board: ({value}: any) => {
      return value === '1' ? 'Yes' : 'No'
    },
    is_present: ({value}: any) => {
      switch (String(value)) {
        case '0':
          return 'Absent'
        case '1':
          return 'Present'
        case '2':
          return 'Waiting'
        case '3':
          return 'Wrong Boarded'

        default:
          break
      }
    },
    checkin_status: ({row}: any) => {
      if (row.original.is_present === '0') return 'Absent'
      return String(row.original.checkin_status) === '1'
        ? `Checkin at ${formatTime(row.original.checkin_time)}`
        : 'Not Checked In'
    },
    checkout_status: ({row}: any) => {
      if (row.original.is_present === '0') return 'Absent'
      return String(row.original.checkout_status) === '1'
        ? `Checkout at ${formatTime(row.original.checkout_time)}`
        : 'Not Checked Out'
    },
  }

  const filteredData = useMemo(() => {
    return studentData.filter((item) => {
      const matchesSearch =
        item.student_name.toLowerCase().includes(searchInput.toLowerCase()) ||
        (item.roll_number && item.roll_number.toLowerCase().includes(searchInput.toLowerCase()))

      const matchesStatus = filterOption === '' || String(item.is_present) === filterOption

      return matchesSearch && matchesStatus
    })
  }, [studentData, searchInput, filterOption])

  return (
    <div>
      <Row className="mt-5 mb-n9 px-10 pb-4 border-bottom">
        <Col sm={9}>
          <InputGroup size="sm">
            <InputGroup.Text id="search">Search</InputGroup.Text>
            <FormControl
              placeholder="Search by Student name or Roll number"
              aria-label="Search"
              aria-describedby="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col sm={3}>
          <InputGroup size="sm">
            <InputGroup.Text>
              <i className="bi-filter"></i>
            </InputGroup.Text>
            <Form.Select
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value)}
              aria-label="Filter options"
            >
              <option value="">Filter by status</option>
              <option value="0">Absent</option>
              <option value="1">Present</option>
              <option value="2">Waiting</option>
              <option value="3">Wrong Boarded</option>
            </Form.Select>
          </InputGroup>
        </Col>
      </Row>
      <div style={{fontSize: '0.9rem'}}>
        <CRUDTable
          cellModifier={cellModifier}
          formSize="sm"
          isSearchable={false}
          fieldToLabelMap={fieldsToLableMap}
          data={filteredData || []}
          noDataMessage="No Records Found"
        />
      </div>
    </div>
  )
}

export default StudentsInDailyTripTable
